import React, {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAutoModalSuspension } from '../../../../common/AutoModalProvider';
import { CompanyVerificationRequest } from '../../../../common/interfaces/entities';
import { useCompanyVerificationModalsContext } from '../../../CompanyVerificationModals/CompanyVerificationModalsProvider';
import ErrorBoundaryWithCrashReporting from '../../../Error/ErrorBoundaryWithCrashReporting';
import { UpdateCompanyVerificationFormState } from '../types';
import {
  getUpdateCompanyVerificationModalData,
  UpdateCompanyVerificationModalData,
} from '../updateCompanyVerificationModalData';

const UpdateCompanyVerificationModal = lazy(
  () =>
    import(
      '../components/UpdateCompanyVerificationForm/UpdateCompanyVerificationModal'
    )
);

type UpdateCompanyVerificationModalProps = {
  lastVerificationRequest: CompanyVerificationRequest;
};

type UpdateCompanyVerificationModalContextType = {
  openModal: (data: UpdateCompanyVerificationModalProps) => void;
};

export const UpdateCompanyVerificationModalContext = createContext<
  UpdateCompanyVerificationModalContextType | undefined
>(undefined);

export function useUpdateCompanyVerificationModal() {
  const context = useContext(UpdateCompanyVerificationModalContext);
  if (!context) {
    throw new Error(
      'useUpdateCompanyVerificationModal must be used within a UpdateCompanyVerificationModalContextProvider'
    );
  }
  return context;
}

export function UpdateCompanyVerificationModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  useAutoModalSuspension(isModalOpen);
  const [modalData, setModalData] =
    useState<UpdateCompanyVerificationModalData | null>(null);
  const methods = useForm<UpdateCompanyVerificationFormState>();
  const { openRejectionModal } = useCompanyVerificationModalsContext();

  const openModal = useCallback(
    ({ lastVerificationRequest }: UpdateCompanyVerificationModalProps) => {
      const modalData = getUpdateCompanyVerificationModalData(
        lastVerificationRequest
      );

      // If there is no mismatch, open the rejection modal directly instead of prompting user to update info
      if (!modalData) {
        openRejectionModal({ lastVerificationRequest });
        return;
      }

      // Set default values to the extracted values from the uploaded legal document.
      methods.reset({
        companyName: modalData.hasCompanyNameMismatch
          ? modalData.document.acraBizFileMetadata?.companyName ||
            modalData.document.companyNpwpDocumentMetadata?.companyName ||
            ''
          : '',
        companyUniqueEntityNumber:
          modalData.hasCompanyUniqueEntityNumberMismatch
            ? modalData.document.acraBizFileMetadata?.uen || ''
            : '',
      });

      setIsModalOpen(true);
      setModalData(modalData);
    },
    [methods, openRejectionModal]
  );

  return (
    <UpdateCompanyVerificationModalContext.Provider
      value={{
        openModal,
      }}
    >
      <ErrorBoundaryWithCrashReporting fallback={null}>
        <Suspense fallback={null}>
          {isModalOpen && modalData && (
            <FormProvider {...methods}>
              <UpdateCompanyVerificationModal
                {...modalData}
                closeModal={() => setIsModalOpen(false)}
              />
            </FormProvider>
          )}
        </Suspense>
      </ErrorBoundaryWithCrashReporting>
      {children}
    </UpdateCompanyVerificationModalContext.Provider>
  );
}
